// src/components/Dashboard/AdjusterHome.jsx
//
// Inicio del panel del ajustador (y demás roles sin admin): cards con sus
// números, lo que necesita su atención, reclamos por fase, próximas citas y
// los reclamos que más tiempo llevan en el mismo paso. Lee lo mismo que su
// lista de reclamos (services/directoryData.js), nada de otras compañías.
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { LanguageContext } from '../../App';
import { useAuth } from '../../contexts/AuthContext';
import { loadDirectoryChecks, loadDirectoryRecords } from '../../services/directoryData';
import { adjusterHomeSummary } from '../../utils/adjusterHome';
import UpcomingMeetingsWidget from '../UpcomingMeetingsWidget';
import './OverviewCards.css';
import './AdjusterHome.css';

const money = value => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value || 0);
const ATTENTION_LIMIT = 6;

const AdjusterHome = ({ lockedCompany, lockedAdjuster, onNavigate }) => {
  const { language } = useContext(LanguageContext);
  const es = language === 'es';
  const t = (spanish, english) => (es ? spanish : english);
  const location = useLocation();
  const { currentUser, userAdjusterName, userRoleKey, userCompanies, can } = useAuth();
  const csrMode = userRoleKey === 'csr_assistant';
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setFailed(false);
    const scope = { csrMode, userCompanies, lockedCompany, lockedAdjuster };
    Promise.all([loadDirectoryChecks(scope), loadDirectoryRecords(scope)])
      .then(([checks, claimRecords]) => { if (!cancelled) setData({ checks, claimRecords }); })
      .catch(error => { console.error('Error loading adjuster home:', error); if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- userCompanies cambia de referencia en cada render
  }, [csrMode, lockedCompany, lockedAdjuster, attempt]);

  const summary = useMemo(() => (data ? adjusterHomeSummary({ ...data, language }) : null), [data, language]);
  const name = (userAdjusterName || currentUser?.displayName || '').split(/\s+/)[0];
  const todayText = new Date().toLocaleDateString(es ? 'es-ES' : 'en-US', { weekday: 'long', day: 'numeric', month: 'long' });
  const today = todayText.charAt(0).toUpperCase() + todayText.slice(1);
  const from = `${location.pathname}${location.search}`;

  if (failed) {
    return (
      <div role="alert" className="ah-error">
        <p>{t('No se pudo cargar tu inicio.', 'Could not load your home page.')}</p>
        <button type="button" className="ov-btn" onClick={() => setAttempt(value => value + 1)}>{t('Reintentar', 'Try again')}</button>
      </div>
    );
  }
  if (!summary) return <div className="cd-loading" role="status"><div className="spinner"></div></div>;

  const pending = summary.attention.length;
  const maxPhase = Math.max(1, ...summary.phases.map(phase => phase.count));
  const attentionCopy = {
    overdue: { icon: 'fa-solid fa-clock', tone: 'is-danger', action: t('Abrir', 'Open') },
    deadline: { icon: 'fa-solid fa-hourglass-half', tone: 'is-warning', action: t('Abrir', 'Open') },
    decision: { icon: 'fa-solid fa-circle-exclamation', tone: 'is-warning', action: t('Decidir', 'Decide') },
    rd: { icon: 'fa-solid fa-rotate-left', tone: 'is-info', action: t('Abrir', 'Open') }
  };
  const attentionDetail = item => {
    if (item.kind === 'rd') return t(`Depreciación recuperable por pedir · ${money(item.amount)}`, `Recoverable depreciation to request · ${money(item.amount)}`);
    if (item.kind === 'decision') return `${item.what}${item.days !== null && item.days !== undefined ? ` · ${t(`esperando tu decisión hace ${item.days} día(s)`, `waiting on you for ${item.days} day(s)`)}` : ''}`;
    if (item.kind === 'overdue') return `${item.what} · ${t('plazo vencido', 'deadline passed')}`;
    return `${item.what} · ${t('plazo por vencer', 'deadline coming up')}`;
  };

  return (
    <div className="ov ah">
      <header className="ah-greeting">
        <h2>{name ? t(`Hola, ${name}`, `Hi, ${name}`) : t('Hola', 'Hi')}</h2>
        <p>
          <span className="ah-date">{today}</span>
          {' · '}
          {pending === 0 ? t('Nada pendiente por ahora', 'Nothing pending right now')
            : pending === 1 ? t('1 cosa espera por ti', '1 thing is waiting for you')
            : t(`${pending} cosas esperan por ti`, `${pending} things are waiting for you`)}
        </p>
      </header>

      <section className="ov-kpis" aria-label={t('Tus números', 'Your numbers')}>
        <article className="ov-card">
          <p className="ov-label">{t('Reclamos activos', 'Active claims')}</p>
          <p className="ov-value">{summary.active}</p>
          <p className="ov-foot">
            <span>{t(`${summary.total} en total`, `${summary.total} in total`)}</span>
            {summary.newThisMonth > 0 && <span className="ov-tag is-up">+{summary.newThisMonth} {t('este mes', 'this month')}</span>}
          </p>
        </article>
        <article className="ov-card">
          <p className="ov-label">{t('Esperan tu decisión', 'Waiting on your decision')}</p>
          <p className="ov-value">{summary.awaiting}</p>
          <p className="ov-foot">
            <span>{summary.awaiting > 0 ? t(`el más antiguo: ${summary.oldestDecision} día(s)`, `oldest: ${summary.oldestDecision} day(s)`) : t('Estás al día', 'You are up to date')}</span>
            {summary.awaiting > 0 && <span className="ov-tag is-warning">{t('Revisar', 'Review')}</span>}
          </p>
        </article>
        <article className="ov-card">
          <p className="ov-label">{t('Plazos que vencen', 'Deadlines due')}</p>
          <p className="ov-value">{summary.deadlines}</p>
          <p className="ov-foot">
            <span>{summary.overdue > 0 ? t(`${summary.overdue} ya vencido(s)`, `${summary.overdue} overdue`) : t('Ninguno vencido', 'None overdue')}</span>
            {summary.overdue > 0 && <span className="ov-tag is-down">{summary.overdue}</span>}
          </p>
        </article>
        {can('viewEarnings') ? (
          <article className="ov-card ov-card-highlight">
            <p className="ov-label">{t('Neto de tus P.A', 'Your P.A net')}</p>
            <p className="ov-value">{money(summary.money.paNet)}</p>
            <p className="ov-foot"><span>{t(`Honorarios ${money(summary.money.paGross)} − CSR ${money(summary.money.csrOwed)}`, `Fees ${money(summary.money.paGross)} − CSR ${money(summary.money.csrOwed)}`)}</span></p>
          </article>
        ) : can('viewOwnEarnings') ? (
          // P.A y asistente: honorarios − comisión de CSR. Sin deuda con CSR
          // (eso es del P.A Admin) y sin el reparto con su compañía, que el
          // sistema no conoce.
          <article className="ov-card ov-card-highlight">
            <p className="ov-label">{t('Tu ganancia', 'Your earnings')}</p>
            <p className="ov-value">{money(summary.money.paNet)}</p>
            <p className="ov-foot"><span>{t(`Honorarios ${money(summary.money.paGross)} − comisión CSR ${money(summary.money.csrOwed)}`, `Fees ${money(summary.money.paGross)} − CSR commission ${money(summary.money.csrOwed)}`)}</span></p>
            <p className="ov-foot ah-note"><span>{t('Antes de lo que acuerdes con tu empresa.', 'Before your split with your company.')}</span></p>
          </article>
        ) : (
          <article className="ov-card ov-card-highlight">
            <p className="ov-label">{t('Cheques recibidos', 'Checks received')}</p>
            <p className="ov-value">{money(summary.money.amount)}</p>
            <p className="ov-foot"><span>{t(`${summary.money.checks} cheque(s), monto bruto`, `${summary.money.checks} check(s), gross amount`)}</span></p>
          </article>
        )}
      </section>

      <section className="ov-row-3 ah-row">
        <article className="ov-card ov-span-2">
          <div className="ah-card-head">
            <h3 className="ov-card-title">{t('Necesitan tu atención', 'Needs your attention')}</h3>
            {pending > ATTENTION_LIMIT && <button type="button" className="ah-link" onClick={() => onNavigate?.('claimstatus')}>{t(`Ver los ${pending}`, `See all ${pending}`)} →</button>}
          </div>
          {pending === 0 ? (
            <p className="ov-foot"><span>{t('No hay decisiones, plazos ni depreciaciones pendientes.', 'No pending decisions, deadlines or depreciation.')}</span></p>
          ) : (
            <ul className="ov-list ah-attention">
              {summary.attention.slice(0, ATTENTION_LIMIT).map(item => (
                <li key={item.key}>
                  <span className={`ah-icon ${attentionCopy[item.kind].tone}`} aria-hidden="true"><i className={attentionCopy[item.kind].icon} /></span>
                  <div className="ah-grow"><strong>{item.clientName || '—'}</strong><span>{attentionDetail(item)}</span></div>
                  <Link className="ah-link" to={`/claim-status/claim/${item.claimRecordId}`} state={{ from }}>{attentionCopy[item.kind].action} →</Link>
                </li>
              ))}
            </ul>
          )}
        </article>
        <article className="ov-card ah-phases">
          <h3 className="ov-card-title">{t('Reclamos activos por fase', 'Active claims by phase')}</h3>
          <ul>
            {summary.phases.map(phase => (
              <li key={phase.id}>
                <span>{phase.label[es ? 'es' : 'en']}</span>
                <span className="ov-bar" aria-hidden="true"><span style={{ width: `${phase.count / maxPhase * 100}%` }} /></span>
                <strong>{phase.count}</strong>
              </li>
            ))}
          </ul>
        </article>
        <UpcomingMeetingsWidget variant="card" />
      </section>

      <section className="ov-card ah-claims" aria-labelledby="ah-claims-title">
        <div className="ah-card-head">
          <h3 id="ah-claims-title" className="ov-card-title">{t('Más tiempo en el mismo paso', 'Longest in the same step')}</h3>
          <button type="button" className="ah-link" onClick={() => onNavigate?.('claimstatus')}>{t('Ver todos mis reclamos', 'See all my claims')} →</button>
        </div>
        {summary.longestInStep.length === 0 ? (
          <p className="ov-foot"><span>{t('No tienes reclamos activos.', 'You have no active claims.')}</span></p>
        ) : (
          <ul className="ah-claim-rows">
            {summary.longestInStep.map(row => (
              <li key={row.id}>
                <Link to={`/claim-status/claim/${row.id}`} state={{ from }}>
                  <span className="ah-claim-client"><strong>{row.clientName || '—'}</strong><span>{row.claimNumber ? `Claim # ${row.claimNumber}` : t('Sin Claim #', 'No Claim #')}{row.insurer ? ` · ${row.insurer}` : ''}</span></span>
                  <span className="ah-claim-step">
                    {row.step || t('Sin paso', 'No step')}
                    <span className="ah-steps" aria-hidden="true">{[1, 2, 3, 4].map(phase => <i key={phase} className={row.phase && phase <= row.phase ? 'is-done' : ''} />)}</span>
                  </span>
                  <span className="ah-claim-days">{row.days !== null ? t(`${row.days} día(s) en el paso`, `${row.days} day(s) in step`) : '—'}</span>
                  <i className="fa-solid fa-chevron-right" aria-hidden="true" />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
};

export default AdjusterHome;
